import _ from 'lodash';
import moment from 'moment';
import { addBusinessDays } from 'date-fns';
import { getRailcardName, getStationName, getTicketTypeName } from 'services/DataService';
import {
  FulfilmentType,
  PostalDeliveryMethodId,
  MIN_POSTAL_DELIVERY_DATES,
  FavouriteSeasonExpiringSoonThresholdDays
} from 'config';

export async function enrichFavouritesResponse (api, favourites) {
  let enrichedFavourites = [];

  // Favour for-of loop over map()
  //  - ensures async calls are called in order
  //  - resulting in succesful service caching
  for (const favourite of favourites) {
    let {
      origin,
      destination,
      ticketType,
      railcards,
      outwardPlusBusOrigin,
      outwardPlusBusDestination,
      returnPlusBusOrigin,
      returnPlusBusDestination
    } = favourite;

    origin = {
      nlc: origin,
      name: await getStationName(api, origin)
    };

    destination = {
      nlc: destination,
      name: await getStationName(api, destination)
    };

    ticketType = {
      code: ticketType,
      name: await getTicketTypeName(api, ticketType)
    };

    railcards = await enrichRailcardsData(api, railcards);

    const plusBus = { outwardPlusBusOrigin, outwardPlusBusDestination, returnPlusBusOrigin, returnPlusBusDestination };
    delete favourite.outwardPlusBusOrigin;
    delete favourite.outwardPlusBusDestination;
    delete favourite.returnPlusBusOrigin;
    delete favourite.returnPlusBusDestination;

    const lastUsed = favourite.dateLastUsed;
    delete favourite.dateLastUsed;

    enrichedFavourites.push({
      ...favourite,
      origin,
      destination,
      ticketType,
      railcards,
      plusBus,
      lastUsed,
    });
  }

  return enrichedFavourites;
}

async function enrichRailcardsData (api, railcards) {
  return await Promise.all(
    railcards.map(async railcardCode => ({
      code: railcardCode,
      name: await getRailcardName(api, railcardCode)
    }))
  );
}

// Firstly sort by season tickets expiring soon,
// then by date last used
export function sortFavourites (favourites) {
  return favourites.sort((a, b) => {
    // Compare season ticket expiry
    let compareExpiringSoon = 0;
    if (isExpiringSoon(a) && !isExpiringSoon(b)) {
      compareExpiringSoon = -1;
    } else if (!isExpiringSoon(a) && isExpiringSoon(b)) {
      compareExpiringSoon = 1;
    } else if (isExpiringSoon(a) && isExpiringSoon(b)) {
      compareExpiringSoon = getDaysToExpiry(a) - getDaysToExpiry(b);
    }

    // Compare date last used
    const lastUsedA = a.lastUsed ? moment(a.lastUsed).date() : null;
    const lastUsedB = b.lastUsed ? moment(b.lastUsed).date() : null;
    let compareLastUsed = 0;
    if (lastUsedA && !lastUsedB) {
      compareLastUsed = -1;
    } else if (!lastUsedA && lastUsedB) {
      compareLastUsed = 1;
    } else if (lastUsedA && lastUsedB) {
      compareLastUsed = lastUsedB - lastUsedA;
    }

    // Last resort, sort by most recently created
    const compareMostRecentlyCreated = b.id - a.id;

    return compareExpiringSoon || compareLastUsed || compareMostRecentlyCreated;
  });
}

function getDaysToExpiry (favourite) {
  return favourite.seasonExpiry
    && moment(favourite.seasonExpiry).date() - moment().date();
}

function isExpiringSoon (favourite) {
  if (!favourite.seasonExpiry) { return false; }

  const expiryDate = moment(favourite.seasonExpiry).date();
  const todayDate = moment().date();
  const dateDiff = expiryDate - todayDate;

  const isUpcoming = expiryDate >= todayDate;
  const withinThreshold = 0 <= dateDiff && dateDiff < FavouriteSeasonExpiringSoonThresholdDays;

  return isUpcoming && withinThreshold;
}

export function groupRailcardsByQuantity (railcards) {
  return railcards.reduce((acc, railcard) => {
    const index = acc.findIndex(existingRailcard => existingRailcard.code === railcard.code);

    if (index === -1) {
      acc.push({ ...railcard, quantity: 1 });
    } else {
      acc[index].quantity++;
    }

    return acc;
  }, []);
}

export function enhanceTicketTypeName ({ code, name }, duration) {
  // Strip '1st Class' from name since we have the class badge displayed
  const ticketTypeName = name.replace(' (1st Class)', '');
  switch (code) {
    case '7DS':
    case '7DF':
      return 'Weekly Season Ticket';
    case 'PSS':
    case 'PSF':
      if (duration.includes('P1M')) {
        return 'Monthly Season Ticket';
      } else if (duration.includes('P12M')) {
        return 'Annual Season Ticket';
      } else {
        return 'Custom Season Ticket';
      }
    case 'FL1':
      return 'Flexi Season Ticket';
    default:
      return ticketTypeName;
  }
}

export function getMinPostalDate (delivery) {
  const { fulfilmentMethod, deliveryMethodId } = delivery;
  if (fulfilmentMethod !== FulfilmentType.POSTAL) { return false; }

  const postalType = _.findKey(PostalDeliveryMethodId, id => id === deliveryMethodId);
  if (!postalType) { return false; }

  return addBusinessDays(new Date(), MIN_POSTAL_DELIVERY_DATES[postalType]);
}


export function checkIfFavourite (fare, favourite) {
  return (
      favourite.origin.nlc === fare.origin.nlc &&
      favourite.destination.nlc === fare.destination.nlc &&
      favourite.adults === fare.adults &&
      favourite.children === fare.children &&
      favourite.route === fare.route.code &&
      favourite.ticketType.code == fare.ticketType.code &&
      favourite.isSeason == fare.ticketType.isSeason &&
      favourite.isOpenReturn == fare.ticketType.isReturn &&
      (favourite.duration && favourite.ticketType.isSeason && !favourite.ticketType.isFlexi
          ? favourite.duration[0] === fare.duration
          : true)
  );
}
